import React, { useEffect, useRef, memo, FC } from 'react';
import classNames from 'classnames';

import useDisableBodyScroll from 'toolkit/useDisableBodyScroll';
import IconButton from 'ui/IconButton';
import IconX from 'ui/icons/X';

import styles from './index.module.scss';

export enum ModalSize {
  SMALL,
  MEDIUM,
  LARGE,
  FULL_HEIGHT_LARGE,
}

export type ModalScreenProps = React.PropsWithChildren<{
  title?: React.ReactNode;
  titlePlacement?: 'left' | 'center';
  titleClassName?: string;
  actionsPlacement?: 'left' | 'center' | 'right';
  titleIcon?: React.ReactNode;
  className?: string;
  disableScroll?: boolean;
  containerClassName?: string;
  contentClassName?: string;
  contentElementId?: string;
  hideCloseButton?: boolean;
  actionButtons?: React.ReactNode[] | React.ReactNode;
  cornerText?: string;
  onClose?: () => void;
  modalSize?: ModalSize;
  containerActionButtonsClassName?: string;
  withBackground?: boolean;
  elementId?: string;
}>;

const ModalScreen: FC<ModalScreenProps> = ({
  title,
  titlePlacement = 'left',
  titleClassName,
  actionsPlacement,
  titleIcon,
  children,
  className,
  disableScroll = true,
  containerClassName,
  contentClassName,
  contentElementId,
  actionButtons = [],
  cornerText,
  hideCloseButton = false,
  onClose,
  modalSize = ModalSize.SMALL,
  containerActionButtonsClassName,
  withBackground = false,
  elementId = 'modal_screen',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const bodyScroll = useDisableBodyScroll();

  useEffect(() => {
    if (disableScroll) {
      bodyScroll.disableBodyScroll(modalContentRef.current!);
    } else {
      bodyScroll.enableBodyScroll(modalContentRef.current!);
    }

    return () => {
      bodyScroll.enableBodyScroll(modalContentRef.current!);
    };
  }, [disableScroll]);

  return (
    <>
      {withBackground && <div className={styles.background} />}

      <div
        data-testid={elementId}
        className={classNames(styles.modalScreen, className)}
        ref={modalRef}>
        <section
          role="dialog"
          className={classNames(
            styles.container,
            containerClassName,
            modalSize === ModalSize.MEDIUM && styles.containerMedium,
            modalSize === ModalSize.LARGE && styles.containerLarge,
            modalSize === ModalSize.FULL_HEIGHT_LARGE && styles.containerFullHeightLarge
          )}>
          {cornerText && <div className={styles.corner}>{cornerText}</div>}

          {!hideCloseButton && (
            <IconButton
              elementId="button_modal_screen_close"
              className={styles.closeButton}
              onClick={onClose}>
              <IconX />
            </IconButton>
          )}

          {titleIcon && <div className={styles.iconContainer}>{titleIcon}</div>}

          {title && (
            <h3
              className={classNames(styles.title, titleClassName)}
              data-placement={titlePlacement}>
              {title}
            </h3>
          )}

          {children && (
            <div
              data-testid={contentElementId}
              className={classNames(
                styles.content,
                contentClassName,
                modalSize === ModalSize.FULL_HEIGHT_LARGE && styles.contentFullHeightLarge
              )}
              ref={modalContentRef}>
              {children}
            </div>
          )}

          <div
            key={`${elementId}_action_buttons`}
            className={classNames(styles.actionButtons, containerActionButtonsClassName)}
            data-placement={actionsPlacement || titlePlacement}>
            {actionButtons}
          </div>
        </section>
      </div>
    </>
  );
};

export default memo(ModalScreen);
